import { useState } from 'react';
import clsx from 'clsx';
import { CheckboxField } from '@clay-run/glaze/checkbox-field';
import { Popover, PopoverPanel, PopoverTrigger } from '@clay-run/glaze/popover';
import { SelectField } from '@clay-run/glaze/select-field';
import { AUTOFILL_IGNORE_PROPS } from '../../../../lib/autofill';
import type { FilterOption } from '@shared/flow/account-hierarchy-deal-model';
import { SIDEBAR_SEARCH_THRESHOLD, filterOptionsByQuery } from './sidebarLayout';
import { useMenuEscapeGuard } from './useMenuEscapeGuard';

/** A caption over a full-width control, the shape of every field in the Filters sidebar. */
export function SidebarField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-xs font-medium text-content-secondary">
        {label}
        {hint && <span className="ml-1 font-normal text-content-tertiary">{hint}</span>}
      </span>
      {children}
    </div>
  );
}

/**
 * Pick several: a full-width field showing the selection ("Open, Won", "3 selected", "All") that opens
 * a popover of checkboxes (a search box once there are more than `SIDEBAR_SEARCH_THRESHOLD` options).
 * Holds an Escape layer while open so Escape closes only the popover, also inside full screen.
 */
export function MultiSelectField({ label, summary, active, options, isChecked, onToggle }: {
  label: string;
  summary: string;
  /** The filter differs from its default: the field reads as pressed. */
  active: boolean;
  options: readonly FilterOption[];
  isChecked: (key: string) => boolean;
  onToggle: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  useMenuEscapeGuard(open);
  const searchable = options.length > SIDEBAR_SEARCH_THRESHOLD;
  const shown = searchable ? filterOptionsByQuery(options, query) : options;
  return (
    <SidebarField label={label}>
      <Popover open={open} onOpenChange={next => { setOpen(next); if (!next) setQuery(''); }}>
        <PopoverTrigger
          render={(
            <button
              type="button"
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-label={`${label}: ${summary}`}
              data-active={active ? 'true' : undefined}
              className={clsx(
                'flex h-8 w-full items-center justify-between gap-2 rounded-md border px-2 text-left text-sm text-content-primary hover:bg-bg-primary-hover',
                active ? 'border-border-action bg-bg-selected' : 'border-border-primary bg-bg-primary',
              )}
            >
              <span className="min-w-0 truncate">{summary}</span>
              <span aria-hidden className="text-content-tertiary">▾</span>
            </button>
          )}
        />
        <PopoverPanel positioner={{ align: 'start' }} aria-label={`${label} filter`} className="w-64 [--popover-padding:8px]">
          <div className="flex flex-col gap-1" role="group" aria-label={label}>
            {searchable && (
              <input
                {...AUTOFILL_IGNORE_PROPS}
                type="search"
                autoFocus
                aria-label={`Search ${label.toLowerCase()}`}
                placeholder="Search"
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="mb-1 h-8 w-full rounded-md border border-border-primary bg-bg-primary px-2 text-sm text-content-primary placeholder:text-content-tertiary"
              />
            )}
            <div className="flex max-h-64 flex-col gap-1 overflow-y-auto">
              {shown.map(o => (
                <CheckboxField key={o.key} size="xs" label={o.label} checked={isChecked(o.key)} onCheckedChange={() => onToggle(o.key)} />
              ))}
              {shown.length === 0 && <span className="text-xs text-content-tertiary">No matches</span>}
            </div>
          </div>
        </PopoverPanel>
      </Popover>
    </SidebarField>
  );
}

/** Pick one: a Glaze Select, with the same Escape layer while its list is open. */
export function SingleSelectField<T extends string>({ label, hint, value, items, onChange }: {
  label: string;
  hint?: string;
  value: T;
  items: { value: T; label: string }[];
  onChange: (next: T) => void;
}) {
  const [open, setOpen] = useState(false);
  useMenuEscapeGuard(open);
  return (
    <SidebarField label={label} hint={hint}>
      <SelectField<T | null>
        aria-label={label}
        size="sm"
        items={items}
        value={value}
        onOpenChange={setOpen}
        onValueChange={(next: T | null) => { if (next) onChange(next); }}
      />
    </SidebarField>
  );
}
